import { useState, useCallback, useMemo, useEffect } from 'react';
import { Col} from 'react-bootstrap';

import { Select } from 'shared/form/components/Select';
import { useNotifications } from 'shared/notifications';
import { useCoreService, useReporteService, ReportFormat } from 'core/services/useCoreService';
import { SelectOption } from 'shared/form/models/SelectOption';
import { DateRangeFilter } from '../DateRangeFilter/DateRangeFilter';
import { FormatSelector } from '../FormatSelector/FormatSelector';
import { Tarjeta } from '../../../tarjeta/models/Tarjeta';
import { useFileDownloader } from 'shared/hooks/useFileDownloader';
import { generateReportFileName } from '../../util/fileNameGenerator';
import { ReportFormActions } from '../ReportFormActions/ReportFormActions';


interface FormData {
  tarjetaId: number | null;
  formato: SelectOption<ReportFormat> | null;
  fechaDesde: string;
  fechaHasta: string;
}

const getDefaultDates = (): { desde: string, hasta: string } => {
  const now = new Date();
  const year = now.getFullYear();
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const day = now.getDate().toString().padStart(2, '0');

  // Usar la fecha actual como valor inicial para 'Hasta' en formato YYYY-MM-DD
  const today = `${year}-${month}-${day}`;
  // Usar el primer día del mes actual como valor inicial para 'Desde' en formato YYYY-MM-DD
  const firstOfMonth = `${year}-${month}-01`;

  return { desde: firstOfMonth, hasta: today };
};

export const MovimientosPorTarjetaForm = () => {
  const defaultDates = useMemo(() => getDefaultDates(), []);
  const { generateMovimientosPorTarjetaReport } = useReporteService();
  const { addNotification } = useNotifications();
  const coreService = useCoreService();
  const { downloadFile } = useFileDownloader();
  const [cardOptions, setCardOptions] = useState<SelectOption<number>[]>([]);
  const [allCards, setAllCards] = useState<Tarjeta[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCards, setIsLoadingCards] = useState(true);

  const [formData, setFormData] = useState<FormData>({
    tarjetaId: null,
    formato: null,
    fechaDesde: defaultDates.desde,
    fechaHasta: defaultDates.hasta,
  });

  useEffect(() => {
    const fetchCards = async () => {
      setIsLoadingCards(true);
      try {
        const response = await coreService.getAllTarjetas();
        setAllCards(response.content);
        setCardOptions(
          response.content.map(
            (card: Tarjeta) =>
              ({ value: card.id, label: `**** ${card.numero.slice(-4)}, Denominación: ${card.denominacion}` }),
          ),
        );
      } catch (error) {
        addNotification('No se pudieron cargar las tarjetas.', 'error');
        setCardOptions([]);
        setAllCards([]);
      } finally {
        setIsLoadingCards(false);
      }
    };
    fetchCards();
  }, [coreService, addNotification]);


  const handleInputChange = useCallback((name: keyof FormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleGenerateReport = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);

    if (!formData.tarjetaId || !formData.formato) {
      addNotification('Debe seleccionar una tarjeta y un formato.', 'warning');
      setIsLoading(false);
      return;
    }

    try {
      const fileData = await generateMovimientosPorTarjetaReport({
        id: formData.tarjetaId,
        formato: formData.formato.value,
        fechaDesde: formData.fechaDesde,
        fechaHasta: formData.fechaHasta,
      });

      // --- 2. Construimos el nombre del archivo ---
      const selectedCard = allCards.find(card => card.id === formData.tarjetaId);

      const fileName = generateReportFileName({
        reporteBase: 'ReporteMovimientosPorTarjeta',
        identificador: selectedCard?.numeroCuenta || 'N-A',
        denominacion: selectedCard?.denominacion || 'SinDenominacion',
        fecha: new Date(formData.fechaHasta),
        extension: formData.formato.value,
      });

      // --- 3. Llamamos a la función de descarga del hook ---
      downloadFile(fileData, fileName);

      addNotification('Reporte generado exitosamente. La descarga ha comenzado.', 'success');
    } catch (error) {
      console.error('Error al generar el reporte:', error);
      addNotification('Hubo un error al generar el reporte.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearForm = () => {
    setFormData({
      tarjetaId: null,
      formato: null,
      fechaDesde: defaultDates.desde,
      fechaHasta: defaultDates.hasta,
    });
  };

  return (
    <form onSubmit={handleGenerateReport}>

        <Col className="mb-4">
          <Select
            label="Tarjeta"
            name="tarjetaId"
            options={cardOptions}
            value={cardOptions.find((option) => option.value === formData.tarjetaId) || null}
            onChange={(option: SelectOption<number>) => {
              handleInputChange('tarjetaId', option?.value ?? null);
            }}
            placeholder={isLoadingCards ? 'Cargando tarjetas...' : 'Seleccione una tarjeta'}
            isDisabled={isLoadingCards}
          />
        </Col>

        <Col className="mb-4">
          <FormatSelector
            value={formData.formato}
            onChange={(option) => handleInputChange('formato', option)}
          />
        </Col>

      <DateRangeFilter
        fechaDesde={formData.fechaDesde}
        fechaHasta={formData.fechaHasta}
        onDesdeChange={(value) => handleInputChange('fechaDesde', value)}
        onHastaChange={(value) => handleInputChange('fechaHasta', value)}
      />

      <ReportFormActions onClear={handleClearForm} isLoading={isLoading} />
    </form>
  );
};